import { mergeAttributes, Node } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { getCellsForSelection, getCellsInColumn, isRowSelected, parseBackgroundColorFromHTML, selectRow } from "./utils";

export interface TableCellOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableCell: {
      /** Set the background color of the selected cell(s) (whole row/column when grip-selected), or clear it with null. */
      setCellsBackgroundColor: (color: string | null) => ReturnType;
    };
  }
}

export const TableCell = Node.create<TableCellOptions>({
  name: "tableCell",

  // No nested tables or Page Breaks in a table cell — pdf-report-core can't render a nested
  // table, and a Page Break here breaks the enclosing table rather than the page (DMS-2004).
  content: "(paragraph | heading | bulletList | orderedList | imageBlock)+",

  tableRole: "cell",

  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [{ tag: "td" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["td", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addAttributes() {
    return {
      colspan: {
        default: 1,
        parseHTML: (element) => {
          const colspan = element.getAttribute("colspan");
          const value = colspan ? parseInt(colspan, 10) : 1;

          return value;
        },
      },
      rowspan: {
        default: 1,
        parseHTML: (element) => {
          const rowspan = element.getAttribute("rowspan");
          const value = rowspan ? parseInt(rowspan, 10) : 1;

          return value;
        },
      },
      colwidth: {
        default: null,
        parseHTML: (element) => {
          const colwidth = element.getAttribute("colwidth");
          const value = colwidth ? [parseInt(colwidth, 10)] : null;

          return value;
        },
      },
      backgroundColor: {
        default: null,
        parseHTML: (element) => parseBackgroundColorFromHTML(element as HTMLElement),
        renderHTML: (attributes) =>
          attributes.backgroundColor ? { style: `background-color: ${attributes.backgroundColor}` } : {},
      },
    };
  },

  addCommands() {
    return {
      setCellsBackgroundColor:
        (color: string | null) =>
        ({ state, dispatch }) => {
          const cells = getCellsForSelection(state.selection);

          if (!cells || !cells.length) {
            return false;
          }

          // Normalize an invalid-hex "" (from Colorpicker's onChange("") on blur) to null so
          // the attr only ever holds a real color or null, never an empty-but-truthy-looking string.
          const normalizedColor = color || null;

          if (dispatch) {
            let tr = state.tr;
            cells.forEach(({ pos, node }) => {
              if (node) {
                tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, backgroundColor: normalizedColor });
              }
            });
            dispatch(tr);
          }

          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const { isEditable } = this.editor;

    return [
      new Plugin({
        props: {
          decorations: (state) => {
            if (!isEditable) {
              return DecorationSet.empty;
            }

            const { doc, selection } = state;
            const decorations: Decoration[] = [];
            const cells = getCellsInColumn(0)(selection);

            if (cells) {
              cells.forEach(({ pos }: { pos: number }, index: number) => {
                decorations.push(
                  Decoration.widget(pos + 1, () => {
                    const rowSelected = isRowSelected(index)(selection);
                    let className = "grip-row";

                    if (rowSelected) {
                      className += " selected";
                    }

                    if (index === 0) {
                      className += " first";
                    }

                    if (index === cells.length - 1) {
                      className += " last";
                    }

                    const grip = document.createElement("a");

                    grip.className = className;
                    grip.addEventListener("mousedown", (event) => {
                      event.preventDefault();
                      event.stopImmediatePropagation();

                      this.editor.view.dispatch(selectRow(index)(this.editor.state.tr));
                    });

                    return grip;
                  })
                );
              });
            }

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});
